import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import { canCreateTask, canBulkUpload } from "../../../config/rbac";
import Loader from "../../../components/Loader";
import Pagination, { usePagination } from "../../../components/Pagination";
import CreateTaskModal from "../../../components/CreateTaskModal";
import FilterChip from "../../../components/FilterChip";
import StatusPill from "../../../components/StatusPill";
import EmptyState from "../../../components/EmptyState";
import Toast, { useToast } from "../../../components/Toast";
import ProjectSheetView from "../../../components/ProjectSheetView";
import ProjectDashboardView from "../../../components/ProjectDashboardView";
import { formatDate } from "../../../config/utils";

const PAGE_SIZE = 10;

export default function ProjectDetailsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const user = getUser();
  const role = user?.role || "Staff";
  const canAdd = canCreateTask(role);
  const canBulk = canBulkUpload(role);
  // Edit: Manager, Head, CXO, Director, Admin (mirrors backend Authorize)
  const canEdit = ["Manager", "Head", "CXO", "Director", "Admin"].includes(role);
  // Delete: Admin only
  const canDelete = role === "Admin";

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("sheet"); // "sheet" | "tasks" | "dashboard"
  const [phases, setPhases] = useState([]);
  const [phaseError, setPhaseError] = useState("");
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [showAddTask, setShowAddTask] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteTimerRef = useRef(null);
  const { toast, showToast, dismissToast } = useToast();

  async function load() {
    setLoading(true);
    setError("");
    setPhaseError("");
    try {
      const [pRes, tRes, phRes] = await Promise.all([
        apiFetch(`/api/projects/${projectId}`),
        apiFetch(`/api/tasks/visible?projectId=${projectId}&limit=500`),
        apiFetch(`/api/projects/${projectId}/phases`),
      ]);

      if (!pRes.ok) throw new Error(await pRes.text());
      if (!tRes.ok) throw new Error(await tRes.text());

      const p = await pRes.json();
      const t = await tRes.json();
      const items = Array.isArray(t) ? t : (t.items || []);
      setTotalTaskCount(Array.isArray(t) ? t.length : (t.totalCount || items.length));

      if (phRes.ok) {
        const ph = await phRes.json();
        setPhases(Array.isArray(ph) ? ph : []);
      } else {
        setPhases([]);
        setPhaseError("Could not load phases. Tasks will appear as unphased.");
      }

      setProject(p);
      setTasks(items);
    } catch (e) {
      setError(e?.message || "Failed to load project");
      setProject(null);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filteredTasks = useMemo(() => {
    let out = tasks;
    if (statusFilter) out = out.filter((t) => t.status === statusFilter);
    if (priorityFilter) out = out.filter((t) => t.priority === priorityFilter);
    return out;
  }, [tasks, statusFilter, priorityFilter]);

  const completedCount = tasks.filter(t => t.status === "Completed").length;
  const progressPct = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const hasFilter = statusFilter || priorityFilter;

  const { totalPages, safePage, paginated } = usePagination(filteredTasks, PAGE_SIZE);
  const currentPage = safePage(page);
  const pagedTasks = paginated(currentPage);

  function openEdit() {
    setEditName(project?.name || "");
    setEditDescription(project?.description || "");
    setEditUrl(project?.projectUrl || "");
    setEditError("");
    setShowEdit(true);
  }

  async function saveEdit() {
    setEditError("");
    if (!editName.trim()) return setEditError("Project name is required.");
    if (editUrl.trim() && !/^https?:\/\//i.test(editUrl.trim()))
      return setEditError("Reference URL must start with http:// or https://");
    setSaving(true);
    try {
      const res = await apiFetch(`/api/projects/${projectId}`, {
        method: "PUT",
        body: JSON.stringify({ name: editName.trim(), description: editDescription || null, projectUrl: editUrl.trim() || null }),
      });
      if (!res.ok) return setEditError(await res.text() || "Failed to save.");
      setShowEdit(false);
      load();
    } catch (e) {
      setEditError(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  function handleDelete() {
    setDeleteError("");

    // Cancel any existing pending delete before scheduling a new one
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }

    // Show undo toast — user has 5 seconds to cancel
    showToast(`"${project?.name}" will be deleted.`, "success", {
      label: "Undo",
      onClick: () => {
        if (deleteTimerRef.current) {
          clearTimeout(deleteTimerRef.current);
          deleteTimerRef.current = null;
        }
      },
    });

    // Schedule actual delete after 5 seconds
    deleteTimerRef.current = setTimeout(async () => {
      deleteTimerRef.current = null;
      setDeleting(true);
      try {
        const res = await apiFetch(`/api/projects/${projectId}`, { method: "DELETE" });
        if (!res.ok) {
          const msg = await res.text();
          setDeleteError(msg || "Failed to delete project.");
        } else {
          navigate("/projects");
        }
      } catch (e) {
        setDeleteError(e?.message || "Failed to delete project.");
      } finally {
        setDeleting(false);
      }
    }, 5000);
  }

  return (
    <div>
      <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 6 }}>
        <button
          className="btn-ghost"
          onClick={() => navigate("/projects")}
          style={{ padding: "6px 10px", borderRadius: 10, fontSize: 13 }}
        >
          ← Projects
        </button>
        {project?.name && (
          <>
            <span style={{ color: "var(--text-3)", fontSize: 13 }}>/</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {project.name}
            </span>
          </>
        )}
      </div>

      {loading ? (
        <div className="glass-card" style={{ padding: 24 }}>
          <Loader inline /> Loading...
        </div>
      ) : error ? (
        <div className="glass-card" style={{ padding: 24, color: "var(--danger-text)" }}>
          {error}
        </div>
      ) : (
        <>
          <div className="glass-card" style={{ padding: 24, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h2 style={{ marginTop: 0, marginBottom: 6 }}>{project?.name}</h2>
                {project?.description ? (
                  <div style={{ color: "var(--text-2)" }}>{project.description}</div>
                ) : (
                  <div style={{ color: "var(--text-2)" }}>No description</div>
                )}
                {project?.projectUrl && /^https?:\/\//i.test(project.projectUrl) && (
                  <a
                    href={project.projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13, color: "#2563eb", textDecoration: "none", display: "inline-block", marginTop: 6 }}
                  >
                    🔗 Open Reference Link
                  </a>
                )}
                {tasks.length > 0 && (
                  <div style={{ marginTop: 14, maxWidth: 320 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-2)", marginBottom: 5 }}>
                      <span>{completedCount} of {tasks.length} tasks completed</span>
                      <span style={{ fontWeight: 700, color: progressPct >= 70 ? "#16a34a" : progressPct >= 30 ? "#b45309" : "#dc2626" }}>{progressPct}%</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 4, background: "var(--border-strong)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${progressPct}%`, background: progressPct >= 70 ? "#22c55e" : progressPct >= 30 ? "#f59e0b" : "#ef4444", borderRadius: 4, transition: "width 0.4s ease" }} />
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {canEdit && (
                  <button
                    className="btn-ghost"
                    style={{ padding: "8px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 13 }}
                    onClick={openEdit}
                  >
                    ✏️ Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleting}
                    style={{ padding: "8px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 13, borderRadius: 8, border: "1px solid #fecdd3", background: "var(--danger-bg)", color: "var(--danger-text)", cursor: "pointer" }}
                  >
                    {deleting ? "Deleting..." : "🗑 Delete"}
                  </button>
                )}
                {canAdd && (
                  <button
                    className="btn"
                    style={{ padding: "9px 18px", fontWeight: 600, whiteSpace: "nowrap" }}
                    onClick={() => setShowAddTask(true)}
                  >
                    + Add Task
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tab bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", gap: 2, background: "var(--surface-2, #f3f4f6)", borderRadius: 10, padding: 3 }}>
              {[{ id: "sheet", label: "📋 Sheet" }, { id: "tasks", label: "☰ List" }, { id: "dashboard", label: "📊 Dashboard" }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{ padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: activeTab === tab.id ? "var(--surface, white)" : "transparent", color: activeTab === tab.id ? "var(--text)" : "var(--text-3)", boxShadow: activeTab === tab.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.15s" }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {activeTab === "tasks" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Filter</span>
                <FilterChip label="Status" value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }}
                  options={[
                    { value: "Open", label: "Open" },
                    { value: "InProgress", label: "In Progress" },
                    { value: "Hold", label: "On Hold" },
                    { value: "UnderValidation", label: "Under Validation" },
                    { value: "Completed", label: "Completed" },
                  ]}
                />
                <FilterChip label="Priority" value={priorityFilter} onChange={(v) => { setPriorityFilter(v); setPage(1); }}
                  options={[
                    { value: "High", label: "High" },
                    { value: "Medium", label: "Medium" },
                    { value: "Low", label: "Low" },
                  ]}
                />
                {hasFilter && (
                  <button
                    onClick={() => { setStatusFilter(""); setPriorityFilter(""); setPage(1); }}
                    style={{ fontSize: 12, color: "var(--text-2)", background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6 }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {deleteError && (
            <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
              {deleteError}
            </div>
          )}

          <CreateTaskModal
            open={showAddTask}
            projectId={projectId}
            onClose={() => setShowAddTask(false)}
            onCreated={() => { setShowAddTask(false); load(); }}
          />

          {/* Edit Modal */}
          {showEdit && (
            <div className="modal-overlay" onClick={() => setShowEdit(false)}>
              <div className="glass-card modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Edit Project</h3>
                  <button className="modal-close" onClick={() => setShowEdit(false)}>✕</button>
                </div>
                {editError && (
                  <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                    {editError}
                  </div>
                )}
                <div style={{ display: "grid", gap: 14 }}>
                  <div>
                    <label className="form-label">Project Name *</label>
                    <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: "100%", marginTop: 4 }} />
                  </div>
                  <div>
                    <label className="form-label">Description</label>
                    <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={3}
                      style={{ width: "100%", marginTop: 4, padding: 8, borderRadius: 8, border: "1px solid var(--border-strong)", fontFamily: "inherit", fontSize: 13, resize: "vertical" }} />
                  </div>
                  <div>
                    <label className="form-label">Reference URL</label>
                    <input value={editUrl} onChange={e => setEditUrl(e.target.value)} placeholder="https://..." style={{ width: "100%", marginTop: 4 }} />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-ghost" onClick={() => setShowEdit(false)}>Cancel</button>
                  <button className="btn" onClick={saveEdit} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirm Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
              <div className="glass-card" style={{ width: 400, padding: 28, borderRadius: 18, boxShadow: "0 25px 60px rgba(0,0,0,0.14)" }} onClick={e => e.stopPropagation()}>
                <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: 16, fontWeight: 700 }}>Delete Project?</h3>
                <p style={{ color: "var(--text-2)", fontSize: 13, marginBottom: 22 }}>
                  Delete <strong>{project?.name}</strong>? All tasks linked to this project will be unlinked. You'll have 5 seconds to undo.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button className="btn-ghost" style={{ padding: "9px 16px", borderRadius: 10 }} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                  <button
                    style={{ padding: "9px 16px", borderRadius: 10, fontWeight: 600, background: "var(--danger-bg)", border: "1px solid #fecdd3", color: "var(--danger-text)", cursor: "pointer" }}
                    disabled={deleting}
                    onClick={() => { setShowDeleteConfirm(false); handleDelete(); }}
                  >
                    {deleting ? "Deleting..." : "Delete Project"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "sheet" ? (
            <ProjectSheetView
              projectId={projectId}
              tasks={tasks}
              canBulkUpload={canBulk}
              phases={phases}
              phaseError={phaseError}
              totalTaskCount={totalTaskCount}
              canManage={canEdit}
              onRefresh={load}
            />
          ) : activeTab === "dashboard" ? (
            <ProjectDashboardView
              project={project}
              tasks={tasks}
              phases={phases}
            />
          ) : (
            <div className="glass-card" style={{ padding: 0 }}>
              <div className="tasks-table">
                <div className="tasks-row header">
                  <div>Task</div>
                  <div>Assignee</div>
                  <div>Due Date</div>
                  <div>Status</div>
                </div>

                {filteredTasks.length === 0 ? (
                  <EmptyState
                    icon="📋"
                    title={hasFilter ? "No tasks match your filters" : "No tasks linked yet"}
                    subtitle={hasFilter ? "Try adjusting or clearing your filters." : "Tasks assigned to this project will appear here."}
                  />
                ) : (
                  pagedTasks.map((t) => (
                    <div
                      key={t.id}
                      className="tasks-row"
                      onClick={() => navigate(`/tasks/${t.id}`)}
                    >
                      <div className="task-title" title={t.title}>{t.title}</div>
                      <div style={{ color: "var(--text-2)" }}>
                        {t.assignedToUserName || "—"}
                      </div>
                      <div className="due-date">{formatDate(t.dueDate)}</div>
                      <div>
                        <StatusPill status={t.status} />
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => setPage(safePage(p))}
                total={filteredTasks.length}
                pageSize={PAGE_SIZE}
              />
            </div>
          )}
        </>
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
